import { CallbackPositionProperty, CallbackProperty, Cartesian2, Cartesian3, Color, ColorMaterialProperty, ConstantProperty, defined, Ellipsoid, Entity, HorizontalOrigin, Matrix3, Quaternion, ScreenSpaceEventType, VerticalOrigin } from "cesium";
import { removeAllScreenSpaceInputActions, toggleEscapeToolListener } from "../map-tools";
import { get } from "svelte/store";
import { mapToolsStore } from "../map-tools-store";
import { toast } from "$lib/components/organisms/toast/toast";
import { busyStateMessage, useCesiumViewer } from "../cesium-viewer";
import { entityIsPartOfMastpoint, getEntityPropertyBag, handleContextualizedCzmlResponse, type PropertyBagEntityTypes, setColorProperty } from "../map-utils";
import type { ContextualizedCzmlResponse } from "$lib/models/IProjectModel";
import api from "$lib/services/kyAuthenticatedInstance";
import { getPlanQueryParameter } from "../map-mode-store";
import type { IMastPointMinimalModel } from "$lib/models/IMastPointMinimalModel";
import { truncateNumber } from "$lib/utils/utils";
import { useDataSources } from "../cesium-datasources";
import { OriginalCesiumInputActions } from "../cesium-original-input-actions";
import { ENTITY_ID_PREFIX } from "$lib/utils/constants";
import { confirmModal } from "$lib/components/organisms/confirmModal/confirmModal";
import { collectGroupEntities, getGroupRoot } from "./utils";

/**
 * Per-pole preview state for the construction code-path. One PolePreview is
 * built per pole entity in the construction, and updated as the user drags
 * the height delta — the preview cylinder grows/shrinks from the pole's
 * terrain base by `(origLength + delta)`.
 */
type PolePreview = {
    /** Original pole entity in the live/plan datasource — coloured grey while previewing. */
    original: Entity;
    /** Cloned cylinder rendered into temp_referenceDataSource. */
    preview: Entity;
    /** ECEF position of the pole's base (terrain). */
    basePos: Cartesian3;
    /** Pole's world-space axis (unit vector) — its gradient. Preserved across delta changes. */
    axis: Cartesian3;
    /** ECEF position of the pole's top (basePos + axis·origLength). */
    origTop: Cartesian3;
    /** Original cylinder length (metres). */
    origLength: number;
    /** Backed-up original cylinder material so we can restore on cleanup. */
    origMaterial: unknown;
    /** Backed-up original cylinder outline so we can restore on cleanup. */
    origOutline: unknown;
};

type ConstructionSession = {
    rootMastpoint: Entity;
    poles: PolePreview[];
    /** Cursor's projected vertical height (above mastpoint base) at click — defines delta=0. */
    referenceHeight: number;
    /** Mastpoint base in ECEF — the cursor's vertical-projection origin. */
    baseCartesian: Cartesian3;
    /** Live delta value in metres (positive = grow, negative = shrink). */
    delta: number;
    /**
     * Original visible height (m) — read from the mastpoint property bag's
     * `data.visibleHeight`. Falls back to the tallest pole's length if the
     * property isn't present on the entity.
     */
    originalHeight: number;
    /**
     * The most-vertical pole's axis — used as the translation direction for
     * every pole's top when the user drags height. Choosing it this way (vs.
     * geodetic up) makes the "straightest" pole's base stay anchored: its
     * `newBase = newTop - axis*newLength` collapses to `origBase` exactly
     * when its axis matches the translation direction. Other poles' bases
     * then shift to preserve their own gradient.
     */
    primaryAxis: Cartesian3;
    label: Entity;
};

// ── Module-scoped state ─────────────────────────────────────────────────────
// `toggleQuickAdjustHeightMastpoint` is called twice per session — once to
// activate, once to deactivate. Closure-locals would be a fresh `undefined`
// on every call, so the cleanup path couldn't see what the activate path set
// up (leaving the mastpoint stuck grey). Hoist all session state into one
// shared object that both invocations reach.
type AdjustHeightState = {
    outlinedMastPoint?: Entity;
    labelEntity?: Entity;
    mastPointCalulcatedHeight?: number;
    baseCartesian?: Cartesian3;
    mastPointVisualCopy?: Entity;
    /**
     * For old single-cylinder mastpoints we grey out the original while the
     * preview is active. Stash material/outline up-front so we can restore
     * them on cleanup — `entityColorPicker` was unreliable from area-mode
     * (silently leaving the cylinder grey).
     */
    cylinderOrigEntity?: Entity;
    cylinderOrigMaterial?: unknown;
    cylinderOrigOutline?: unknown;
    /** Original cylinder length (metres) — needed to compute the delta on commit. */
    cylinderOrigLength?: number;
    /** Construction-mode session — non-null when the user picked a mastpoint construction (point graphics, child poles). */
    constructionSession: ConstructionSession | null;
    mouseMoveDelayActive: boolean;
    /**
     * Freezes the live preview/label while the confirm modal is open — without
     * this, cursor movement behind the modal keeps changing the very value the
     * user is being asked to confirm.
     */
    previewPaused: boolean;
};

/**
 * Mast-height bounds (metres). Both code paths (old cylinder + constructions)
 * clamp the live preview so the user can't drag below ground or past a sane
 * upper limit before committing. Server enforces its own validation; these
 * are just guard-rails for the visualisation.
 */
const MIN_HEIGHT_M = 0.1;
const MAX_HEIGHT_M = 100;

const state: AdjustHeightState = {
    constructionSession: null,
    mouseMoveDelayActive: false,
    previewPaused: false
};

const toggleQuickAdjustHeightMastpoint = () => {
    const viewer = useCesiumViewer();
    const screenSpaceEventHandler = viewer.screenSpaceEventHandler;
    removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);

    mapToolsStore.disableAllToolsExceptToggleToolByName('quickAdjustMastPointHeight', viewer);
    const { temp_referenceDataSource } = useDataSources();

    const killswitch = () => {
        // Old cylinder mastpoint: restore the stashed original material/outline directly.
        if (state.cylinderOrigEntity?.cylinder) {
            state.cylinderOrigEntity.cylinder.material = state.cylinderOrigMaterial as never;
            state.cylinderOrigEntity.cylinder.outline = state.cylinderOrigOutline as never;
        }
        state.cylinderOrigEntity = undefined;
        state.cylinderOrigMaterial = undefined;
        state.cylinderOrigOutline = undefined;
        state.cylinderOrigLength = undefined;

        // Construction: restore each pole's stashed material/outline.
        if (state.constructionSession) {
            for (const p of state.constructionSession.poles) {
                if (p.original.cylinder) {
                    p.original.cylinder.material = p.origMaterial as never;
                    p.original.cylinder.outline = p.origOutline as never;
                }
            }
        }
        state.constructionSession = null;
        state.labelEntity = undefined;
        state.mastPointCalulcatedHeight = undefined;
        state.baseCartesian = undefined;
        state.mastPointVisualCopy = undefined;
        state.outlinedMastPoint = undefined;
        state.previewPaused = false;
        mapToolsStore.update(store => {
            store.selectedMapEntities = [];
            return store
        });

        temp_referenceDataSource.entities.removeAll();
    }

    /**
     * Project a window position onto the vertical axis through `base`, returning
     * the height (metres) of the cursor above the base. Same math as the
     * cylinder-mode `updateScreenToWorldMeasurement` body.
     */
    const projectCursorHeightAboveBase = (windowPosition: Cartesian2, base: Cartesian3): number | null => {
        const ray = viewer.camera.getPickRay(windowPosition);
        if (!ray) return null;
        const cameraPos = viewer.camera.position;
        const rayDir = Cartesian3.normalize(ray.direction, new Cartesian3());
        const up = Ellipsoid.WGS84.geodeticSurfaceNormal(base, new Cartesian3());

        const camToBase = Cartesian3.subtract(base, cameraPos, new Cartesian3());
        const dv = Cartesian3.dot(camToBase, up);
        const dw = Cartesian3.dot(camToBase, rayDir);
        const wv = Cartesian3.dot(rayDir, up);

        if (Math.abs(wv) < 0.9999) {
            const u = (dv * wv - dw) / ((wv * wv) - 1);
            return u * wv - dv;
        }
        return 0;
    }

    /**
     * Walk a child pole's centred position back to its terrain base, using
     * the pole's *own* axis (not geodetic up) — leaning poles in A-frame
     * constructions store an orientation quaternion that tilts the cylinder
     * along the lean direction. If we used geodetic up here, the preview
     * cylinder's bottom would drift off the terrain as length changed
     * (and connected tops would visibly cross/gap).
     *
     * The world-axis derivation matches `rotateMastpoint.getMastAxisWorld`.
     */
    const computePoleBase = (poleEntity: Entity): { base: Cartesian3; up: Cartesian3; length: number } | null => {
        const pos = poleEntity.position?.getValue() as Cartesian3 | undefined;
        const length = poleEntity.cylinder?.length?.getValue() as number | undefined;
        if (!pos || length === undefined) return null;

        let axis = Ellipsoid.WGS84.geodeticSurfaceNormal(pos, new Cartesian3());
        Cartesian3.normalize(axis, axis);
        if (poleEntity.orientation) {
            const q = poleEntity.orientation.getValue() as Quaternion | undefined;
            if (defined(q)) {
                const m = Matrix3.fromQuaternion(q, new Matrix3());
                axis = Matrix3.multiplyByVector(m, new Cartesian3(0, 0, 1), new Cartesian3());
                Cartesian3.normalize(axis, axis);
            }
        }

        const half = Cartesian3.multiplyByScalar(axis, length / 2, new Cartesian3());
        const base = Cartesian3.subtract(pos, half, new Cartesian3());
        return { base, up: axis, length };
    }

    /**
     * Build a per-pole preview clone, anchored to the original pole's terrain
     * base. The preview cylinder will grow/shrink from `basePos` as `delta`
     * changes; the original is greyed out so the preview reads visually.
     */
    const buildPolePreview = (pole: Entity): PolePreview | null => {
        if (!pole.cylinder) return null;
        const computed = computePoleBase(pole);
        if (!computed) return null;
        const { base, up: axis, length } = computed;

        const origMaterial = pole.cylinder.material;
        const origOutline = pole.cylinder.outline;
        pole.cylinder.material = setColorProperty(Color.GRAY.withAlpha(0.3));
        pole.cylinder.outline = new CallbackProperty(() => new ConstantProperty(false), false);

        // Capture the pole's top in world ECEF — `updateConstructionPreview`
        // translates it by `delta` along the session's primaryAxis and
        // recomputes the base from there.
        const origTop = Cartesian3.add(
            base,
            Cartesian3.multiplyByScalar(axis, length, new Cartesian3()),
            new Cartesian3()
        );

        // Initial preview matches the original (delta=0).
        const half = Cartesian3.multiplyByScalar(axis, length / 2, new Cartesian3());
        const center = Cartesian3.add(base, half, new Cartesian3());
        const preview = temp_referenceDataSource.entities.add({
            position: new CallbackPositionProperty(() => center, false),
            orientation: pole.orientation,
            cylinder: {
                length: new ConstantProperty(length),
                topRadius: pole.cylinder.topRadius,
                bottomRadius: pole.cylinder.bottomRadius,
                material: new ColorMaterialProperty(Color.YELLOW.withAlpha(0.6)),
                outline: false
            }
        });

        return { original: pole, preview, basePos: base, axis, origTop, origLength: length, origMaterial, origOutline };
    }

    /**
     * The construction's *top assembly* translates by `delta` along the
     * primary axis (the most-vertical pole's axis), preserving each pole's
     * own gradient. The pole that defines the primary axis stays anchored at
     * its base — only its top moves. Other poles' tops follow the same
     * translation, and their bases shift to preserve their gradient.
     *
     *   newTop    = origTop + primaryAxis * delta
     *   newLength = origLength + delta
     *   newBase   = newTop - axis * newLength
     *
     * For the primary (straightest) pole, axis = primaryAxis, so
     * newBase = origBase — no drift. For leaning poles, the base shifts in
     * the (primaryAxis − axis) direction so the gradient stays put.
     */
    const updateConstructionPreview = (delta: number): void => {
        const session = state.constructionSession;
        if (!session) return;
        session.delta = delta;

        for (const p of session.poles) {
            const newLength = Math.max(0.1, p.origLength + delta);

            const topShift = Cartesian3.multiplyByScalar(session.primaryAxis, delta, new Cartesian3());
            const newTop = Cartesian3.add(p.origTop, topShift, new Cartesian3());

            const axisOffset = Cartesian3.multiplyByScalar(p.axis, newLength, new Cartesian3());
            const newBase = Cartesian3.subtract(newTop, axisOffset, new Cartesian3());

            const newCenter = Cartesian3.add(newBase, newTop, new Cartesian3());
            Cartesian3.divideByScalar(newCenter, 2, newCenter);

            if (p.preview.cylinder) {
                p.preview.cylinder.length = new ConstantProperty(newLength);
            }
            p.preview.position = new CallbackPositionProperty(() => newCenter, false);
            // Orientation preserved — gradient must not change.
        }

        if (session.label.label) {
            const sign = delta > 0 ? '+' : delta < 0 ? '' : '±';
            const actualHeight = session.originalHeight + delta;
            session.label.label.text = new ConstantProperty(
                `${actualHeight.toFixed(1)} m (${sign}${delta.toFixed(1)} m)`
            );
        }
    }

    /** Initialise a construction session for a clicked entity (any descendant of a mastpoint with point graphics). */
    const registerConstructionSelection = (clickedEntity: Entity, clickWindowPos: Cartesian2): boolean => {
        const root = getGroupRoot(clickedEntity);
        if (!root || !root.id?.startsWith(ENTITY_ID_PREFIX.MASTPOINT)) return false;
        const rootPos = root.position?.getValue() as Cartesian3 | undefined;
        if (!rootPos) return false;

        const poles = collectGroupEntities(root, root.entityCollection)
            .filter((e) => e.id.startsWith(ENTITY_ID_PREFIX.POLE) && !!e.cylinder);
        if (poles.length === 0) return false;

        if(!viewer.dataSources.contains(temp_referenceDataSource)) viewer.dataSources.add(temp_referenceDataSource);

        const previews: PolePreview[] = [];
        for (const pole of poles) {
            const preview = buildPolePreview(pole);
            if (preview) previews.push(preview);
        }
        if (previews.length === 0) return false;

        // Cursor's vertical height above the root mastpoint at click defines delta=0.
        const referenceHeight = projectCursorHeightAboveBase(clickWindowPos, rootPos) ?? 0;

        // Prefer the mastpoint's recorded visibleHeight for the label readout —
        // fall back to the tallest pole if the property isn't set on the entity.
        const bag = getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(root);
        const recordedHeight = (bag?.data as unknown as { visibleHeight?: number } | undefined)?.visibleHeight;
        const tallestPoleLength = previews.reduce((max, p) => Math.max(max, p.origLength), 0);
        const originalHeight = typeof recordedHeight === 'number' ? recordedHeight : tallestPoleLength;

        // Pick the translation axis. Two regimes:
        //  - One pole clearly more vertical than the others (e.g. main + support):
        //    use that pole's axis as primary, so it stays anchored at its base.
        //  - All poles lean similarly (A-frame): no anchor exists, so fall back to
        //    geodetic up — every pole's base then shifts symmetrically.
        // The "clearly more vertical" check is a gap test on dot(axis, upRef):
        // the most-vertical must exceed the runner-up by at least ANCHOR_GAP.
        const upRef = Ellipsoid.WGS84.geodeticSurfaceNormal(rootPos, new Cartesian3());
        Cartesian3.normalize(upRef, upRef);
        const ANCHOR_GAP = 0.05;
        let bestIdx = 0;
        let bestDot = -Infinity;
        let secondDot = -Infinity;
        for (let i = 0; i < previews.length; i++) {
            const d = Cartesian3.dot(previews[i].axis, upRef);
            if (d > bestDot) {
                secondDot = bestDot;
                bestDot = d;
                bestIdx = i;
            } else if (d > secondDot) {
                secondDot = d;
            }
        }
        const primaryAxis = bestDot - secondDot > ANCHOR_GAP
            ? previews[bestIdx].axis
            : upRef;

        const labelPos = Cartesian3.clone(rootPos);
        const label = temp_referenceDataSource.entities.add({
            position: new CallbackPositionProperty(() => labelPos, false),
            label: {
                font: '16px Poppins, sans-serif',
                text: `${originalHeight.toFixed(1)} m (±0.0 m)`,
                fillColor: Color.WHITE,
                showBackground: true,
                backgroundColor: Color.BLACK.withAlpha(0.8),
                horizontalOrigin: HorizontalOrigin.CENTER,
                verticalOrigin: VerticalOrigin.BOTTOM,
                pixelOffset: new Cartesian2(0, -10),
                disableDepthTestDistance: Number.POSITIVE_INFINITY
            }
        });

        state.constructionSession = {
            rootMastpoint: root,
            poles: previews,
            referenceHeight,
            baseCartesian: rootPos,
            delta: 0,
            originalHeight,
            primaryAxis,
            label
        };
        return true;
    }

    /**
     * Handles initiation of temporary entities based on mastpoint selection.
     * @param selectedMastPoint 
     * @returns 
     */
    const registerMastPointSelection = (clickedMastpoint: Entity) => {
        const selectedMastPoint = clickedMastpoint;
        // Don't touch `selectedMapEntities` — when invoked from area-mode it
        // already holds the polygon's mastpoints and the area-mode UI reads
        // its count from there. Local state below is enough for the tool.

        if(!selectedMastPoint.cylinder?.length || !selectedMastPoint.position){
            toast({
                type: 'error',
                title: 'Feil ved valg av mastepunkt',
                description: 'Det valgte mastepunktet mangler nødvendig informasjon (høyde eller posisjon). Kontakt SmartGIS hvis feilen vedvarer.'
            });	
            return;
        } 

        const originalMagnitude = Cartesian3.magnitude(selectedMastPoint.position.getValue() as Cartesian3);
        const mastBaseMagnitude = originalMagnitude - (selectedMastPoint.cylinder.length.getValue()) / 2;
        const origScalar = mastBaseMagnitude / originalMagnitude;
        state.baseCartesian = Cartesian3.multiplyByScalar(
            selectedMastPoint.position!.getValue() as Cartesian3,
            origScalar,
            new Cartesian3()
        );

        // Stash original material/outline so we can restore on cleanup, instead
        // of relying on `entityColorPicker` to re-derive — that path was
        // unreliable from area-mode and left the cylinder grey.
        state.cylinderOrigEntity = selectedMastPoint;
        state.cylinderOrigMaterial = selectedMastPoint.cylinder.material;
        state.cylinderOrigOutline = selectedMastPoint.cylinder.outline;
        state.cylinderOrigLength = selectedMastPoint.cylinder.length.getValue() as number;

        selectedMastPoint.cylinder.outline = new CallbackProperty(() => new ConstantProperty(false), false);
        selectedMastPoint.cylinder.material = setColorProperty(Color.GRAY.withAlpha(0.3));
        const visualCopy = new Entity();
        visualCopy.merge(selectedMastPoint);
        state.mastPointVisualCopy = visualCopy;

        if(!viewer.dataSources.contains(temp_referenceDataSource)) viewer.dataSources.add(temp_referenceDataSource);
        temp_referenceDataSource.entities.add(visualCopy);

        state.labelEntity = temp_referenceDataSource.entities.add({
            position: visualCopy.position,
            label: {
                font: '16px Poppins, sans-serif',
                text: selectedMastPoint.cylinder.length.getValue() + " m",
                fillColor: Color.WHITE,
                showBackground: true,
                backgroundColor: Color.BLACK.withAlpha(0.8),
                verticalOrigin: VerticalOrigin.BOTTOM,
                pixelOffset: new Cartesian2(0, -10)
            }
        });
    }

    /**
     * Updates the mastpoint visualization
     * @param heightDifference 
     * @returns 
     */
    const updateVisualCopy = (heightDifference: number) => {
        const visualCopy = state.mastPointVisualCopy;
        const base = state.baseCartesian;
        if (!visualCopy || !visualCopy.cylinder || !base) return;

        state.mastPointCalulcatedHeight = heightDifference;

        const up = Ellipsoid.WGS84.geodeticSurfaceNormal(base, new Cartesian3());
        const half = Cartesian3.multiplyByScalar(up, heightDifference / 2.0, new Cartesian3());
        // Ny senterposisjon for sylinderen: base + halvparten av høyden i opp-retning
        const newCenter = Cartesian3.add(base, half, new Cartesian3());
        visualCopy.position = new CallbackPositionProperty(() => newCenter, false);
        visualCopy.cylinder.length = new ConstantProperty(Math.abs(heightDifference));
        // Oppdater label ved topp-punktet
        const topPoint = Cartesian3.add(base, Cartesian3.multiplyByScalar(up, heightDifference, new Cartesian3()), new Cartesian3());
        const labelEntity = state.labelEntity;
        if(labelEntity && labelEntity.label){
            labelEntity.position = new CallbackPositionProperty(() => topPoint, false);
            labelEntity.label.text = new ConstantProperty(heightDifference + " m");
        }
          // Om ønskelig kan man endre label farge/tekst avhengig av +/-
    }

    /**
     * Calculates vertical height difference between the mouse cursor and the base of the mastpoint.
     * 
     * @param windowPosition 
     * @returns 
     */
    const updateScreenToWorldMeasurement = (windowPosition: Cartesian2) => {
        const base = state.baseCartesian;
        if(!base) return;
        // Få en ray fra kamera gjennom aktuell museposisjon
        const ray = viewer.camera.getPickRay(windowPosition);
        if (!ray){
            return;
        }
        const cameraPos = viewer.camera.position;
        const rayDir = Cartesian3.normalize(ray.direction, new Cartesian3());
        // Up vector of base
        const up = Ellipsoid.WGS84.geodeticSurfaceNormal(base, new Cartesian3());

        // Difference vector between camera and base
        const camToBase = Cartesian3.subtract(base, cameraPos, new Cartesian3());
        const dv = Cartesian3.dot(camToBase, up);       // component of camera - base up
        const dw = Cartesian3.dot(camToBase, rayDir);   // component of camera - base along ray
        const wv = Cartesian3.dot(rayDir, up);          // cos angle between ray and up
      
        let t = 0;
        if (Math.abs(wv) < 0.9999) { 
            // Deduction of t (height difference) and u (along ray) 
            const u = (dv * wv - dw) / ((wv * wv) - 1);
            t = u * wv - dv;
        } else {
            // If ray is ~paralell with up (camera above), avoid divide by 0. Can possibly be improved?
            t = 0;
        }
      
        // Clamp to sane mast-height range so the preview can't go below ground
        // or shoot past the upper limit while the user drags.
        const clamped = Math.min(MAX_HEIGHT_M, Math.max(MIN_HEIGHT_M, t));
        // Round to closest decimal (10. centi)
        const roundedHeightDifference = truncateNumber(clamped, 1);
        updateVisualCopy(roundedHeightDifference);
    };

    if (get(mapToolsStore).quickAdjustMastPointHeight.toolEnabled) {
        toggleEscapeToolListener({toggleOn: true});
        screenSpaceEventHandler.setInputAction(async (event: { position: Cartesian2 }) => {
            const pick = viewer.scene.pick(event.position);
            const pickedEntity = pick?.id instanceof Entity ? (pick.id as Entity) : undefined;
            // Track "already picked" via local session state rather than
            // selectedMapEntities — the global store can be pre-populated
            // (notably by area-mode), which would otherwise block every click.
            const hasSession = !!state.mastPointVisualCopy || !!state.constructionSession;

            if (hasSession || !pickedEntity) return;

            // 1. Old single-cylinder mastpoint: reuse existing flow.
            if (pickedEntity.id?.startsWith(ENTITY_ID_PREFIX.MASTPOINT) && pickedEntity.cylinder) {
                registerMastPointSelection(pickedEntity);
                return;
            }

            // 2. Mastpoint construction (point-graphics root) clicked directly.
            if (pickedEntity.id?.startsWith(ENTITY_ID_PREFIX.MASTPOINT) && pickedEntity.point) {
                registerConstructionSelection(pickedEntity, event.position);
                return;
            }

            // 3. Child entity of a construction (pole / crossbeam / wire / support).
            if (entityIsPartOfMastpoint(getEntityPropertyBag<PropertyBagEntityTypes>(pickedEntity))) {
                registerConstructionSelection(pickedEntity, event.position);
                return;
            }
        }, ScreenSpaceEventType.LEFT_CLICK);

        screenSpaceEventHandler.setInputAction(async (event: { position: Cartesian2 }) => {
            await new Promise((resolve) => setTimeout(() => resolve(true), 50));

            // Construction commit — send the delta value.
            if (state.constructionSession) {
                const session = state.constructionSession;
                const delta = session.delta;
                if (Math.abs(delta) < 0.05) {
                    toast({
                        type: 'info',
                        title: 'Ingen endring',
                        description: `Ingen endring i høyde ble registrert.`
                    });
                    killswitch();
                    return;
                }
                const sign = delta > 0 ? '+' : '';
                state.previewPaused = true;
                confirmModal(
                    `Høyden for hver stolpe i konstruksjonen vil endres med ${sign}${delta.toFixed(1)} m. Er dette korrekt?`,
                    async () => {
                        busyStateMessage.set('Oppdaterer mastepunkt, vennligst vent ...');
                        try {
                            const mastpointId = getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(session.rootMastpoint).data.id;
                            const response: ContextualizedCzmlResponse = await api.put(`MastPoint/UpdateHeight/${mastpointId}/czml${get(getPlanQueryParameter)}`, {
                                json: { visibleHeight: delta }
                            }).json();
                            await handleContextualizedCzmlResponse(response);
                            killswitch();
                        } catch {
                            busyStateMessage.set('');
                            toast({
                                type: 'error',
                                title: 'Feil ved oppdatering av mastepunkt',
                                description: `En feil oppstod ved kontakt med server. Vennligst vent litt og prøv igjen.`
                            });
                            killswitch();
                        }
                        busyStateMessage.set('');
                    },
                    () => { state.previewPaused = false; }
                );
                return;
            }

            // Old cylinder-mastpoint commit — send the *delta* (matches the
            // construction code-path and the server's unified contract).
            const visualCopy = state.mastPointVisualCopy;
            if(defined(state.mastPointCalulcatedHeight) && visualCopy && visualCopy.cylinder?.length && state.cylinderOrigEntity && state.cylinderOrigLength !== undefined){
                const newHeight = visualCopy.cylinder.length.getValue() as number;
                const delta = +(newHeight - state.cylinderOrigLength).toFixed(2);
                const pickedMastpoint = state.cylinderOrigEntity;

                if (Math.abs(delta) < 0.05) {
                    toast({
                        type: 'info',
                        title: 'Ingen endring',
                        description: 'Ingen endring i høyde ble registrert.'
                    });
                    killswitch();
                    return;
                }

                const sign = delta > 0 ? '+' : '';
                state.previewPaused = true;
                confirmModal(
                    `Mastepunktets høyde vil endres med ${sign}${delta.toFixed(1)} m (ny høyde ${newHeight} m). Er dette korrekt?`,
                    async () => {
                        busyStateMessage.set('Oppdaterer mastepunkt, vennligst vent ...');
                        try{
                            const selectedMastPointId = getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(pickedMastpoint).data.id;
                            const response: ContextualizedCzmlResponse = await api.put(`MastPoint/UpdateHeight/${selectedMastPointId}/czml${get(getPlanQueryParameter)}`, {
                                json: {
                                    visibleHeight: delta
                                }
                            }).json();
                            await handleContextualizedCzmlResponse(response);
                            killswitch();
                        } catch (error: unknown) {
                            busyStateMessage.set('');
                            toast({
                                type: 'error',
                                title: 'Feil ved oppdatering av mastepunkt',
                                description: `En feil oppstod ved kontakt med server. Vennligst vent litt og prøv igjen.`
                            })
                            killswitch();
                        }
                        busyStateMessage.set('');
                    },
                    () => { state.previewPaused = false; }
                )
            }else {
                toast({
                    type: 'info',
                    title: 'Ingen endring',
                    description: `Ingen endring i høyde ble registrert.`
                })
                killswitch();
            }
        }, ScreenSpaceEventType.RIGHT_CLICK);

        screenSpaceEventHandler.setInputAction(async (event: { startPosition: Cartesian2, endPosition: Cartesian2 }) => {
            // Freeze preview/label while the confirm modal is open — otherwise
            // the value the user is being asked to confirm would keep shifting.
            if (state.previewPaused) return;
            if (state.constructionSession) {
                const session = state.constructionSession;
                const projected = projectCursorHeightAboveBase(event.endPosition, session.baseCartesian);
                if (projected === null) return;

                // Clamp delta so every pole stays in (MIN_HEIGHT_M, MAX_HEIGHT_M).
                // The shortest pole drives the lower bound (delta can't drop it
                // below ground); the tallest drives the upper bound.
                let minOrigLength = Infinity;
                let maxOrigLength = 0;
                for (const p of session.poles) {
                    if (p.origLength < minOrigLength) minOrigLength = p.origLength;
                    if (p.origLength > maxOrigLength) maxOrigLength = p.origLength;
                }
                const minDelta = MIN_HEIGHT_M - minOrigLength;
                const maxDelta = MAX_HEIGHT_M - maxOrigLength;

                const raw = projected - session.referenceHeight;
                const clamped = Math.min(maxDelta, Math.max(minDelta, raw));
                const delta = truncateNumber(clamped, 1);
                updateConstructionPreview(delta);
                return;
            }
            // Old cylinder mode: drag-to-resize once a mastpoint is registered.
            if(state.cylinderOrigEntity){
                updateScreenToWorldMeasurement(event.endPosition);
            }else {
                // Handle Move event when no mastpoint is selected
                if(!event.endPosition || state.mouseMoveDelayActive) return;
                state.mouseMoveDelayActive = true;

                // Improves performance noticeably. Does not scene pick before a minimum of 50ms in intervals.
                setTimeout(() => {
                    state.mouseMoveDelayActive = false;
                }, 25);

                const pick = viewer.scene.pick(event.endPosition);
                const previouslyOutlinedMastPoint = state.outlinedMastPoint ? state.outlinedMastPoint : undefined;

                if(!defined(pick)){
                    //If no entity is hovered over, remove outline of previous entity and return early. No further action needed.
                    if(previouslyOutlinedMastPoint){
                        previouslyOutlinedMastPoint!.cylinder!.outline = new CallbackProperty(() => new ConstantProperty(false), false);
                    }
                    return;
                }

                if(pick.id instanceof Entity && pick.id.id?.startsWith('MastPoint')){
                    state.outlinedMastPoint = pick.id;
                    
                    if(state.outlinedMastPoint && state.outlinedMastPoint.cylinder){
                        state.outlinedMastPoint!.cylinder!.outline = new CallbackProperty(() => new ConstantProperty(true), false);
                        state.outlinedMastPoint!.cylinder!.outlineWidth = new CallbackProperty(() => new ConstantProperty(2), false);
                        state.outlinedMastPoint!.cylinder!.outlineColor = new CallbackProperty(() => new ColorMaterialProperty(Color.ORANGE), false);
                    }
                    if(previouslyOutlinedMastPoint === state.outlinedMastPoint) return;
                }
                
                if(previouslyOutlinedMastPoint){
                    previouslyOutlinedMastPoint!.cylinder!.outline = new CallbackProperty(() => new ConstantProperty(false), false);
                }
            }
        }, ScreenSpaceEventType.MOUSE_MOVE);

    } else {
        toggleEscapeToolListener({toggleOn: false});
        screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        // Reset the left click input action
        screenSpaceEventHandler.setInputAction(
            OriginalCesiumInputActions.LEFT_CLICK,
            ScreenSpaceEventType.LEFT_CLICK
        );
        killswitch();
    }
}

export { toggleQuickAdjustHeightMastpoint };